import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { useRef } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Popover } from "@volca/ui/components/ui/popover";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { PackChatReplyForm } from "@/payroll/pack-chat/pack-chat-reply-form";
import {
  CellCommentThreadView,
  isInsideCellCommentOverlay,
} from "./cell-comment-thread";
import {
  useReplyToCellCommentThread,
  useStartCellCommentThread,
} from "./query";
import type { ICellCommentTarget, ICellCommentThread } from "./types";
import { useCellCommentActions } from "./use-cell-comment-actions";

export interface CellCommentCardTarget extends ICellCommentTarget {
  // The cell's current grid (display) position, to anchor the card beside it.
  gridCol: number;
  gridRow: number;
  // Label for the card header, e.g. "D12".
  address: string;
  context?: ICellCommentThread["context"];
  // Focus the composer on open: yes when the user asked to comment, no when
  // the card opened because the cursor landed on a commented cell — keyboard
  // navigation must stay on the grid.
  focusComposer: boolean;
}

const POPOVER_POPUP_CLASSNAME =
  "z-50 flex w-72 origin-(--transform-origin) flex-col gap-2.5 rounded-lg bg-surface-overlay p-2.5 text-sm text-ink shadow-overlay outline-hidden duration-100 data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95";

/**
 * The comment card for one cell: its threads, a reply box on the open one, or a
 * box to start a thread when none is open. Resolving sits in the header beside
 * close, since it closes the conversation the same way. Anchored at a screen
 * point, like the grid's context menu, so it floats over the canvas next to the
 * cell.
 */
export const CellCommentCard = ({
  businessId,
  runId,
  target,
  anchor,
  threads,
  onClose,
}: {
  businessId: string | null;
  runId: string;
  target: CellCommentCardTarget | null;
  // Screen point beside the cell, or null while the cell is scrolled out of
  // view (the card hides and comes back with it).
  anchor: { x: number; y: number } | null;
  threads: ICellCommentThread[];
  onClose: () => void;
}) => {
  const start = useStartCellCommentThread(businessId, runId);
  const reply = useReplyToCellCommentThread(businessId, runId);
  const actions = useCellCommentActions(businessId, runId);
  const openThread = threads.find((thread) => !thread.resolved_at);
  const anchorRef = useRef<HTMLSpanElement>(null);

  return (
    <Popover
      open={target !== null && anchor !== null}
      onOpenChange={(open, details) => {
        if (open) return;
        if (
          details.reason === "focus-out" ||
          (details.reason === "outside-press" &&
            isInsideCellCommentOverlay(details.event.target))
        ) {
          details.cancel();
          return;
        }
        onClose();
      }}
      modal={false}
    >
      {/* The anchor follows the cell as the grid scrolls. */}
      <span
        ref={anchorRef}
        aria-hidden
        className="pointer-events-none fixed h-0 w-0"
        style={{ left: anchor?.x ?? 0, top: anchor?.y ?? 0 }}
      />
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Positioner
          anchor={anchorRef}
          align="start"
          side="right"
          sideOffset={4}
          className="isolate z-50"
        >
          <PopoverPrimitive.Popup
            data-slot="popover-content"
            className={cn(POPOVER_POPUP_CLASSNAME, "w-80 gap-0 p-0")}
            initialFocus={false}
            aria-label={target ? `Comments on ${target.address}` : "Comments"}
          >
            <Stack
              direction="row"
              align="center"
              justify="between"
              gap={2}
              className="w-full border-b border-line py-2 pr-2 pl-4"
            >
              <Text
                variant="label"
                className="min-w-0 truncate"
                title={target ? `Comments · ${target.address}` : undefined}
              >
                Comments{target ? ` · ${target.address}` : ""}
              </Text>
              <Stack
                direction="row"
                align="center"
                gap={1}
                className="shrink-0"
              >
                {openThread && (
                  <Button
                    variant="ghost"
                    size="xs"
                    disabled={actions.isUpdating}
                    onClick={() => void actions.resolve(openThread.id)}
                  >
                    <CheckIcon aria-hidden />
                    Resolve
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Close comments"
                  className="text-subtle"
                  onClick={onClose}
                >
                  <XIcon aria-hidden />
                </Button>
              </Stack>
            </Stack>
            <Stack
              gap={4}
              align="stretch"
              className="max-h-96 w-full overflow-y-auto px-4 py-3"
            >
              {threads.map((thread) => (
                <CellCommentThreadView
                  key={thread.id}
                  thread={thread}
                  isUpdating={actions.isUpdating}
                  onDeleteComment={(commentId) =>
                    void actions.deleteComment(thread.id, commentId)
                  }
                  onReopen={() => void actions.reopen(thread.id)}
                />
              ))}
              {target &&
                (openThread ? (
                  <PackChatReplyForm
                    key={openThread.id}
                    autoFocus={target.focusComposer}
                    loading={reply.isPending}
                    onReply={async (body) => {
                      await reply.mutateAsync({
                        threadId: openThread.id,
                        body,
                      });
                    }}
                  />
                ) : (
                  <PackChatReplyForm
                    key={`${target.sheetId}:${target.row}:${target.col}`}
                    autoFocus={target.focusComposer}
                    loading={start.isPending}
                    placeholder="Add a comment"
                    actionLabel="Comment"
                    onReply={async (body) => {
                      await start.mutateAsync({
                        target,
                        body,
                        context: target.context,
                      });
                    }}
                  />
                ))}
            </Stack>
          </PopoverPrimitive.Popup>
        </PopoverPrimitive.Positioner>
      </PopoverPrimitive.Portal>
    </Popover>
  );
};
